import { telemetryIncrementAction } from 'core-events/actions';
import React, { JSX, useContext, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Search } from '@videoblocks/design-system/icons';

import { NavSearchContext, StatsigExperimentContext } from '../../context/NavSearchContext';
import ConversationalSearchTooltip from './ConversationalSearchTooltip';

/**
 * Vivid gradient used by the animated glow that traces the button outline
 * while AI search is available, to draw attention to the feature.
 */
const GLOW_PURPLE = '#9952e0';
const GLOW_PINK = '#ff66e6';
const GLOW_VIOLET = '#b357e1';

// The glow is a one-time attention-grabbing flourish: visible for this long
// after the button first appears, then unmounted so it doesn't keep animating.
// `GLOW_FADE_MS` is carved off the end of that window for a smooth fade-out,
// so the glow is already at opacity 0 by the time it unmounts.
const GLOW_VISIBLE_MS = 2000;
const GLOW_FADE_MS = 600;

const ICON_CLS = 'relative z-10 w-12 self-stretch flex justify-center items-center transition-opacity duration-300';
const HOVER_INTENT_DELAY_MS = 350;

export default function SearchWithAIButton({
    disabledForContentType,
}: {
    disabledForContentType?: boolean;
}): JSX.Element {
    const { conversationalSearchEnabled, setConversationalSearchEnabled: setEnabled } = useContext(NavSearchContext);
    const { conversationalAnimatedToggleEnabled } = useContext(StatsigExperimentContext);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Visual state ignores stale context: when disabled for the current content type,
    // render as off regardless of whether the context has caught up yet.
    const effectivelyEnabled = conversationalSearchEnabled && !disabledForContentType;

    const toggleButtonStyles = [
        'relative h-14 p-1 rounded-full outline-2 -outline-offset-1 inline-flex items-center',
        'transition-colors duration-300 ease-in-out',
    ];

    if (disabledForContentType) {
        toggleButtonStyles.push('cursor-not-allowed outline-gray-500 bg-gray-200 outline-1');
    } else {
        toggleButtonStyles.push(
            'cursor-pointer outline-violet-200 bg-linear-to-r',
            effectivelyEnabled
                ? 'from-yellow-700 via-bubblegum-600 to-blueviolet-500'
                : 'from-yellow-50 via-fuchsia-100 to-violet-100',
        );
    }

    const toggleButton = (
        <button
            data-testid="conversational-search-toggle"
            type="button"
            aria-disabled={disabledForContentType}
            onClick={() => {
                if (!disabledForContentType) {
                    setEnabled((prev) => !prev);
                    void telemetryIncrementAction('conversational_search_toggle_click');
                }
            }}
            onMouseEnter={() => {
                if (disabledForContentType) return;
                hoverTimerRef.current = setTimeout(() => {
                    hoverTimerRef.current = null;
                    void telemetryIncrementAction('conversational_search_toggle_hover');
                }, HOVER_INTENT_DELAY_MS);
            }}
            onMouseLeave={() => {
                if (hoverTimerRef.current !== null) {
                    clearTimeout(hoverTimerRef.current);
                    hoverTimerRef.current = null;
                }
            }}
            aria-pressed={effectivelyEnabled}
            aria-label={
                disabledForContentType
                    ? 'Conversational Search is only available for video, motion backgrounds, and templates.'
                    : effectivelyEnabled
                      ? 'Switch to standard search'
                      : 'Switch to AI search'
            }
            className={twMerge(...toggleButtonStyles)}
        >
            {/* Sliding white pill */}
            <div
                className={twMerge(
                    'absolute top-1 bottom-1 w-12 bg-white rounded-full shadow-lg transition-transform duration-300 ease-in-out',
                    effectivelyEnabled ? 'translate-x-12' : 'translate-x-0',
                )}
            />
            {/* Search icon */}
            <div className={twMerge(ICON_CLS, 'opacity-100', disabledForContentType && 'bg-gray-700 rounded-full')}>
                <Search
                    width={20}
                    height={20}
                    className={twMerge((disabledForContentType || effectivelyEnabled) && '[&_path]:fill-white')}
                />
            </div>
            {/* Sparkles icon (inline SVG for gradient fill) */}
            <div className={twMerge(ICON_CLS, effectivelyEnabled ? 'opacity-100' : 'opacity-50')}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 27 26" width={24} height={24}>
                    <defs>
                        <linearGradient id="sparkle-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop
                                offset="0%"
                                className="transition-all duration-300"
                                stopColor={effectivelyEnabled ? 'var(--color-blueviolet-600)' : 'var(--color-gray-950)'}
                            />
                            <stop
                                offset="100%"
                                className="transition-all duration-300"
                                stopColor={effectivelyEnabled ? 'var(--color-bubblegum-600)' : 'var(--color-gray-950)'}
                            />
                        </linearGradient>
                    </defs>
                    <path
                        fill="url(#sparkle-gradient)"
                        d="M23.25 18a.75.75 0 0 1 .75.75A2.25 2.25 0 0 0 26.25 21a.75.75 0 0 1 0 1.5A2.25 2.25 0 0 0 24 24.75a.75.75 0 0 1-1.5 0 2.25 2.25 0 0 0-2.25-2.25.75.75 0 0 1 0-1.5 2.25 2.25 0 0 0 2.25-2.25.75.75 0 0 1 .75-.75"
                    />
                    <path
                        fill="url(#sparkle-gradient)"
                        d="M9.75 3a.75.75 0 0 1 .75.75A8.25 8.25 0 0 0 18.75 12a.75.75 0 0 1 0 1.5 8.25 8.25 0 0 0-8.25 8.25.75.75 0 0 1-1.5 0A8.25 8.25 0 0 0 .75 13.5a.75.75 0 0 1 0-1.5A8.25 8.25 0 0 0 9 3.75.75.75 0 0 1 9.75 3"
                    />
                    <path
                        fill="url(#sparkle-gradient)"
                        d="M23.25 0a.75.75 0 0 1 .75.75A2.25 2.25 0 0 0 26.25 3a.75.75 0 0 1 0 1.5A2.25 2.25 0 0 0 24 6.75a.75.75 0 0 1-1.5 0 2.25 2.25 0 0 0-2.25-2.25.75.75 0 0 1 0-1.5A2.25 2.25 0 0 0 22.5.75a.75.75 0 0 1 .75-.75"
                    />
                </svg>
            </div>
        </button>
    );

    // The animated glow is purely decorative and would compete for attention
    // with the disabled (greyed-out) state, so it is only shown when usable.
    const trigger = (
        <span className="relative isolate ml-2 inline-flex items-center z-10">
            {!disabledForContentType && conversationalAnimatedToggleEnabled && <AnimatedAIGlow />}
            {toggleButton}
        </span>
    );

    return (
        <ConversationalSearchTooltip
            disabledForContentType={disabledForContentType}
            enabled={conversationalSearchEnabled}
            setEnabled={setEnabled}
            trigger={trigger}
        />
    );
}

/**
 * Decorative glow that traces the toggle's pill outline. A single thick, soft
 * stroke whose gradient is fixed in space (`userSpaceOnUse`) and fades to
 * transparent at both ends, so the color concentrates into blooms on the
 * sides and fades out at the top and bottom rather than forming an even ring.
 * A wide dash with one gap sweeps slowly around to make it shimmer.
 *
 * Rendered behind the button (`-z-10` within the parent's isolated stacking
 * context) so only the portion bleeding past the button's edges is visible.
 * Coordinates are kept in the reference design's 141×89 user space so the
 * fixed gradient lands exactly as designed; that canvas (with its centred
 * ~108×56 pill) is centred over the 104×56 button via negative insets.
 */
function AnimatedAIGlow(): React.ReactElement | null {
    // Show the glow for `GLOW_VISIBLE_MS` after mount, then unmount it so the
    // sweep animation doesn't keep running indefinitely.
    const [visible, setVisible] = useState(true);
    useEffect(() => {
        const timer = setTimeout(() => setVisible(false), GLOW_VISIBLE_MS);
        return () => clearTimeout(timer);
    }, []);

    if (!visible) return null;

    // Open, near-closed pill outline (a hairline gap at bottom-centre, capped
    // round) centred within the 141×89 canvas.
    const pillPath =
        'M83.364 72.5H44.5C29.036 72.5 16.5 59.964 16.5 44.5C16.5 29.036 29.036 16.5 44.5 16.5H96.5C111.964 16.5 124.5 29.036 124.5 44.5C124.5 59.964 111.964 72.5 96.5 72.5H83.8973';

    // some math so that the animation will be a seamless loop
    const dashPeriodPx = 0.9 * Math.sqrt((141 ** 2 + 89 ** 2) / 2) + 50;
    const loopOffsetPx = 6 * dashPeriodPx;
    const loopDurationS = loopOffsetPx / 100;

    return (
        <svg
            aria-hidden="true"
            width={141}
            height={89}
            viewBox="0 0 141 89"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            // Centre the 141×89 canvas over the 104×56 button: offset by half the
            // overflow on each axis ((141-104)/2 = 18.5, (89-56)/2 = 16.5).
            style={{ left: '-18.5px', top: '-16.5px' }}
            className="ai-glow-svg pointer-events-none absolute -z-10"
        >
            <style>{`
                @keyframes ai-glow-sweep {
                    from { stroke-dashoffset: 0; }
                    to { stroke-dashoffset: ${loopOffsetPx}px; }
                }
                @keyframes ai-glow-fadeout {
                    to { opacity: 0; }
                }
                .ai-glow-stroke {
                    stroke-dasharray: 90% 50;
                    animation: ai-glow-sweep ${loopDurationS}s linear infinite;
                }
                .ai-glow-svg {
                    animation: ai-glow-fadeout ${GLOW_FADE_MS}ms linear ${GLOW_VISIBLE_MS - GLOW_FADE_MS}ms forwards;
                }
                @media (prefers-reduced-motion: reduce) {
                    .ai-glow-stroke,
                    .ai-glow-svg { animation: none; }
                }
            `}</style>
            <defs>
                <linearGradient
                    id="ai-glow-gradient"
                    x1="235.242"
                    y1="-29.415"
                    x2="6.14331"
                    y2="5.56662"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={GLOW_PURPLE} stopOpacity={0} />
                    <stop offset={0.504808} stopColor={GLOW_PINK} />
                    <stop offset={0.817257} stopColor={GLOW_VIOLET} />
                    <stop offset={1} stopColor={GLOW_PINK} stopOpacity={0} />
                </linearGradient>
                <filter
                    id="ai-glow-blur"
                    x="0"
                    y="0"
                    width="141"
                    height="89"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feGaussianBlur stdDeviation="6" />
                </filter>
            </defs>
            <g filter="url(#ai-glow-blur)">
                <path
                    className="ai-glow-stroke"
                    d={pillPath}
                    stroke="url(#ai-glow-gradient)"
                    strokeWidth={9}
                    strokeLinecap="round"
                />
            </g>
        </svg>
    );
}
